import { type AbstractEngine, EngineInstrumentation, type Scene, SceneInstrumentation } from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs';
import { Injectable, Zed } from '@modules/core';
import { World } from 'miniplex';

export type PerformanceInfo = {
  fps: number;
  meshSelection: number;
  renderTarget: number;
  render: number;
  frameTotal: number;
  interFrame: number;
  activeMeshes: number;
  drawCalls: number;
  vertices: number;
  resolution: string;
  loadHomeTotal: number;
  loadTextureTotal: number;
  customInfo?: Map<string, Map<string, any>>;
};

export class PerformanceProfiler implements Injectable {
  static readonly INJECTABLE_TOKEN = Symbol.for('PerformanceProfiler');

  private engine: AbstractEngine;

  private sceneInstrumentation: SceneInstrumentation | undefined = undefined;

  private engineInstrumentation: EngineInstrumentation | undefined = undefined;

  private intervalId: NodeJS.Timer | undefined = undefined;

  private customDataMap = new Map<string, Map<string, any>>();

  private readonly scene;
  constructor({
    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
  }: {
    [Zed.WORLD_TOKEN]: World;
    [BabylonjsModule.INJECTABLE_TOKEN]: BabylonjsModule;
  }) {
    this.scene = babylonjs.scene;
    this.engine = this.scene.getEngine();

    PerformanceProfiler.instance = this;
  }

  dispose(): void {
    PerformanceProfiler.instance = undefined;
    this.unregisterCallback();
  }

  // 싱글턴 처리
  private static instance?: PerformanceProfiler | undefined = undefined;

  public static getInstance = (scene: Scene) => {
    return this.instance;
  };
  // END

  public loadHomeTotal = 0;
  public loadTextureTotal = 0;

  public itemChanged = false;

  public addCustom(group: string, item: string, value: any) {
    if (!this.customDataMap.has(group)) {
      this.customDataMap.set(group, new Map<string, any>());
    }
    this.customDataMap.get(group)!.set(item, value);
  }

  public registerCallback(callback: (param: PerformanceInfo, itemChanged?: boolean) => void, intervalMs: number) {
    // 기존에 존재할 경우 unregister
    if (this.intervalId !== undefined) {
      this.unregisterCallback();
    }

    // 측정에 필요한 클래스들 생성
    if (this.sceneInstrumentation === undefined) {
      this.createInstrumentations();
    }

    this.intervalId = setInterval(() => {
      // interval 마다 아래 부분 호출
      const performanceInfo = {
        fps: this.scene.getEngine().getFps(),
        meshSelection: this.sceneInstrumentation!.activeMeshesEvaluationTimeCounter.lastSecAverage,
        renderTarget: this.sceneInstrumentation!.renderTargetsRenderTimeCounter.lastSecAverage,
        render: this.sceneInstrumentation!.renderTimeCounter.lastSecAverage,
        frameTotal: this.sceneInstrumentation!.frameTimeCounter.lastSecAverage,
        interFrame: this.sceneInstrumentation!.interFrameTimeCounter.lastSecAverage,
        activeMeshes: this.scene.getActiveMeshes().length,
        drawCalls: this.sceneInstrumentation?.drawCallsCounter.current!,
        vertices: this.scene.getTotalVertices(),
        resolution: this.engine.getRenderWidth() + ' x ' + this.engine.getRenderHeight(),
        loadHomeTotal: this.loadHomeTotal,
        loadTextureTotal: this.loadTextureTotal,
        customInfo: this.customDataMap,
      };
      callback(performanceInfo, this.itemChanged);
      if (this.itemChanged) {
        this.itemChanged = false;
      }
      // END
    }, intervalMs);
  }

  public unregisterCallback() {
    if (this.intervalId !== undefined) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;

      this.sceneInstrumentation?.dispose();
      this.sceneInstrumentation = undefined;

      this.engineInstrumentation?.dispose();
      this.engineInstrumentation = undefined;
    }
  }

  public showInspector() {
    this.scene.debugLayer.show({ showExplorer: false, initialTab: 2 });
  }

  public hideInspector() {
    this.scene.debugLayer.hide();
  }

  private createInstrumentations = () => {
    this.sceneInstrumentation = new SceneInstrumentation(this.scene);
    this.sceneInstrumentation.captureActiveMeshesEvaluationTime = true;
    this.sceneInstrumentation.captureRenderTargetsRenderTime = true;
    this.sceneInstrumentation.captureFrameTime = true;
    this.sceneInstrumentation.captureRenderTime = true;
    this.sceneInstrumentation.captureInterFrameTime = true;
    this.sceneInstrumentation.captureParticlesRenderTime = true;
    this.sceneInstrumentation.captureSpritesRenderTime = true;
    this.sceneInstrumentation.capturePhysicsTime = true;
    this.sceneInstrumentation.captureAnimationsTime = true;

    this.engineInstrumentation = new EngineInstrumentation(this.scene.getEngine());
    this.engineInstrumentation.captureGPUFrameTime = true;
  };
}
